import React, { useState, useEffect } from "react";
import "../style/Dashboard.css";
import downIcon from "../icons/down.svg";
import { db } from "../firebase-config";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";

const Dashboard = () => {
  const today = new Date().toLocaleDateString();

  const [mostBoughtDropdownVisible, setMostBoughtDropdownVisible] =
    useState(false);
  const [mostBoughtPeriod, setMostBoughtPeriod] = useState("past month");

  const [leastBoughtDropdownVisible, setLeastBoughtDropdownVisible] =
    useState(false);
  const [leastBoughtPeriod, setLeastBoughtPeriod] = useState("past month");

  // Live data states
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalCategories, setTotalCategories] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [mostBoughtItems, setMostBoughtItems] = useState([]);
  const [leastBoughtItems, setLeastBoughtItems] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch statistics data
  useEffect(() => {
    fetchStatistics();
  }, []);

  // Fetch most/least bought items when period changes
  useEffect(() => {
    fetchMostBoughtItems(mostBoughtPeriod);
  }, [mostBoughtPeriod]);

  useEffect(() => {
    fetchLeastBoughtItems(leastBoughtPeriod);
  }, [leastBoughtPeriod]);

  const fetchStatistics = async () => {
    try {
      setIsLoading(true);

      // Fetch total categories
      const categoriesSnapshot = await getDocs(collection(db, "categories"));
      setTotalCategories(categoriesSnapshot.size);

      // Fetch total products across all categories
      let productsCount = 0;
      const productIds = new Set();

      for (const categoryDoc of categoriesSnapshot.docs) {
        const categoryName = categoryDoc.id;
        const itemsCollection = collection(
          db,
          "products",
          categoryName,
          "items"
        );
        const itemsSnapshot = await getDocs(itemsCollection);

        itemsSnapshot.forEach((doc) => {
          const productData = doc.data();
          const productId = productData.uuid || productData.productId || doc.id;
          productIds.add(productId);
        });
      }

      setTotalProducts(productIds.size);

      // Fetch total users
      const usersSnapshot = await getDocs(collection(db, "users"));
      setTotalUsers(usersSnapshot.size);

      // Fetch top customers
      await fetchTopCustomers();

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching statistics:", error);
      setIsLoading(false);
    }
  };

  const getDateRange = (period) => {
    const now = new Date();
    const startDate = new Date();

    if (period === "past week") {
      startDate.setDate(now.getDate() - 7);
    } else {
      // past month
      startDate.setMonth(now.getMonth() - 1);
    }

    return startDate;
  };

  const fetchMostBoughtItems = async (period) => {
    try {
      const startDate = getDateRange(period);

      // Fetch orders from the specified period
      const ordersRef = collection(db, "orders");
      const q = query(
        ordersRef,
        where("createdAt", ">=", startDate),
        orderBy("createdAt", "desc")
      );

      const ordersSnapshot = await getDocs(q);

      // Count product purchases
      const productCounts = {};

      ordersSnapshot.forEach((doc) => {
        const orderData = doc.data();
        const items = orderData.items || [];

        items.forEach((item) => {
          const productKey = item.selectedProductId || item.id;
          if (!productCounts[productKey]) {
            productCounts[productKey] = {
              count: 0,
              name: item.name,
              image: item.image,
              productId: productKey,
            };
          }
          productCounts[productKey].count += 1;
        });
      });

      // Sort by count and get top 4
      const sortedProducts = Object.values(productCounts)
        .sort((a, b) => b.count - a.count)
        .slice(0, 4);

      setMostBoughtItems(sortedProducts);
    } catch (error) {
      console.error("Error fetching most bought items:", error);
      setMostBoughtItems([]);
    }
  };

  const fetchLeastBoughtItems = async (period) => {
    try {
      const startDate = getDateRange(period);

      // Fetch orders from the specified period
      const ordersRef = collection(db, "orders");
      const q = query(
        ordersRef,
        where("createdAt", ">=", startDate),
        orderBy("createdAt", "desc")
      );

      const ordersSnapshot = await getDocs(q);

      // Count product purchases
      const productCounts = {};

      ordersSnapshot.forEach((doc) => {
        const orderData = doc.data();
        const items = orderData.items || [];

        items.forEach((item) => {
          const productKey = item.selectedProductId || item.id;
          if (!productCounts[productKey]) {
            productCounts[productKey] = {
              count: 0,
              name: item.name,
              image: item.image,
              productId: productKey,
            };
          }
          productCounts[productKey].count += 1;
        });
      });

      // Sort by count (ascending) and get bottom 4
      const sortedProducts = Object.values(productCounts)
        .sort((a, b) => a.count - b.count)
        .slice(0, 4);

      setLeastBoughtItems(sortedProducts);
    } catch (error) {
      console.error("Error fetching least bought items:", error);
      setLeastBoughtItems([]);
    }
  };

  const fetchTopCustomers = async () => {
    try {
      const ordersRef = collection(db, "orders");
      const ordersSnapshot = await getDocs(ordersRef);

      // Aggregate customer data
      const customerData = {};

      ordersSnapshot.forEach((doc) => {
        const orderData = doc.data();
        const customerEmail = orderData.customerEmail || orderData.email;
        const customerName = orderData.customerName || "Unknown";
        const totalPrice = orderData.totalPrice || 0;

        if (customerEmail) {
          if (!customerData[customerEmail]) {
            customerData[customerEmail] = {
              name: customerName,
              email: customerEmail,
              orderCount: 0,
              totalPaid: 0,
            };
          }
          customerData[customerEmail].orderCount += 1;
          customerData[customerEmail].totalPaid += parseFloat(totalPrice);
        }
      });

      // Sort by total paid and get top 5
      const sortedCustomers = Object.values(customerData)
        .sort((a, b) => b.totalPaid - a.totalPaid)
        .slice(0, 5);

      setTopCustomers(sortedCustomers);
    } catch (error) {
      console.error("Error fetching top customers:", error);
      setTopCustomers([]);
    }
  };

  const toggleMostBoughtDropdown = () => {
    setMostBoughtDropdownVisible(!mostBoughtDropdownVisible);
  };

  const toggleLeastBoughtDropdown = () => {
    setLeastBoughtDropdownVisible(!leastBoughtDropdownVisible);
  };

  const handleMostBoughtOptionClick = (period) => {
    setMostBoughtPeriod(period);
    setMostBoughtDropdownVisible(false);
  };

  const handleLeastBoughtOptionClick = (period) => {
    setLeastBoughtPeriod(period);
    setLeastBoughtDropdownVisible(false);
  };

  const handleRefresh = () => {
    fetchStatistics();
    fetchMostBoughtItems(mostBoughtPeriod);
    fetchLeastBoughtItems(leastBoughtPeriod);
  };

  return (
    <div className="dashboard-container">
      <div className="date">{today}</div>
      <div className="statistics">Statistics</div>
      <div className="stats-container">
        <div className="stats-box">
          <div className="stats-number">
            {isLoading ? "..." : totalProducts}
          </div>
          <div className="stats-label">Total Products</div>
        </div>
        <div className="stats-box">
          <div className="stats-number">
            {isLoading ? "..." : totalCategories}
          </div>
          <div className="stats-label">Total Categories</div>
        </div>
        <div className="stats-box">
          <div className="stats-number">{isLoading ? "..." : totalUsers}</div>
          <div className="stats-label">Users</div>
        </div>
      </div>
      <div className="additional-containers">
        <div className="container-left">
          <div className="container-header">
            <span>Most bought items</span>
            <div className="dropdown" onClick={toggleMostBoughtDropdown}>
              {mostBoughtPeriod}{" "}
              <img
                src={downIcon}
                alt="Dropdown icon"
                className="dropdown-icon"
              />
              {mostBoughtDropdownVisible && (
                <div className="dropdown-content">
                  <button
                    className="dropdown-item"
                    onClick={() => handleMostBoughtOptionClick("past month")}
                  >
                    past month
                  </button>
                  <button
                    className="dropdown-item"
                    onClick={() => handleMostBoughtOptionClick("past week")}
                  >
                    past week
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="items-container">
            {mostBoughtItems.length > 0 ? (
              mostBoughtItems.map((item, index) => (
                <div key={index} className="item-box">
                  <img
                    src={item.image}
                    alt={item.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              ))
            ) : (
              <>
                <div className="item-box"></div>
                <div className="item-box"></div>
                <div className="item-box"></div>
                <div className="item-box"></div>
              </>
            )}
          </div>
          <div className="item-numbers">
            {mostBoughtItems.length > 0 ? (
              mostBoughtItems.map((item, index) => (
                <div key={index} className="item-number">
                  {item.count}
                </div>
              ))
            ) : (
              <>
                <div className="item-number">-</div>
                <div className="item-number">-</div>
                <div className="item-number">-</div>
                <div className="item-number">-</div>
              </>
            )}
          </div>
        </div>
        <div className="container-right">
          <div className="container-header">
            <span>Least bought items</span>
            <div className="dropdown" onClick={toggleLeastBoughtDropdown}>
              {leastBoughtPeriod}{" "}
              <img
                src={downIcon}
                alt="Dropdown icon"
                className="dropdown-icon"
              />
              {leastBoughtDropdownVisible && (
                <div className="dropdown-content">
                  <button
                    className="dropdown-item"
                    onClick={() => handleLeastBoughtOptionClick("past month")}
                  >
                    past month
                  </button>
                  <button
                    className="dropdown-item"
                    onClick={() => handleLeastBoughtOptionClick("past week")}
                  >
                    past week
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="items-container">
            {leastBoughtItems.length > 0 ? (
              leastBoughtItems.map((item, index) => (
                <div key={index} className="item-box">
                  <img
                    src={item.image}
                    alt={item.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              ))
            ) : (
              <>
                <div className="item-box"></div>
                <div className="item-box"></div>
                <div className="item-box"></div>
                <div className="item-box"></div>
              </>
            )}
          </div>
          <div className="item-numbers">
            {leastBoughtItems.length > 0 ? (
              leastBoughtItems.map((item, index) => (
                <div key={index} className="item-number">
                  {item.count}
                </div>
              ))
            ) : (
              <>
                <div className="item-number">-</div>
                <div className="item-number">-</div>
                <div className="item-number">-</div>
                <div className="item-number">-</div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="new-dashboard-container">
        <div className="new-dashboard-container-header">
          <span>Top purchasing customers</span>
          <span className="refresh" onClick={handleRefresh}>
            Refresh
          </span>
        </div>
        <div className="customer-table-container">
          <div className="customer-table">
            <div className="customer-row customer-header">
              <div className="name">Name</div>
              <div className="order-count">Order count</div>
              <div className="paid">Paid (€)</div>
              <div className="email">Email</div>
            </div>
            {topCustomers.length > 0 ? (
              topCustomers.map((customer, index) => (
                <div key={index} className="customer-row">
                  <div className="name">{customer.name}</div>
                  <div className="order-count">{customer.orderCount}</div>
                  <div className="paid">{customer.totalPaid.toFixed(2)}</div>
                  <div className="email">{customer.email}</div>
                </div>
              ))
            ) : (
              <div className="customer-row">
                <div
                  className="name"
                  style={{ textAlign: "center", gridColumn: "1 / -1" }}
                >
                  {isLoading ? "Loading..." : "No customer data available"}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
